export type ZoneId =
  | 'library'
  | 'hand'
  | 'battlefield'
  | 'graveyard'
  | 'exile'
  | 'command'
  | 'sideboard'
  | 'stack'
  | 'revealed';

export type BattlefieldRow = 'creatures' | 'other' | 'lands';

export type ManaPoolColor = 'W' | 'U' | 'B' | 'R' | 'G' | 'C';

export type CardLayout =
  | 'normal'
  | 'transform'
  | 'modal_dfc'
  | 'split'
  | 'adventure'
  | 'flip'
  | 'meld';

export type GamePhase =
  | 'untap'
  | 'upkeep'
  | 'draw'
  | 'main1'
  | 'combat_begin'
  | 'combat_attack'
  | 'combat_block'
  | 'combat_damage'
  | 'main2'
  | 'end';

export const GAME_PHASES: { id: GamePhase; label: string; short: string }[] = [
  { id: 'untap', label: 'Untap', short: 'UNT' },
  { id: 'upkeep', label: 'Upkeep', short: 'UPK' },
  { id: 'draw', label: 'Draw', short: 'DRW' },
  { id: 'main1', label: 'First Main', short: 'M1' },
  { id: 'combat_begin', label: 'Begin Combat', short: 'CMB' },
  { id: 'combat_attack', label: 'Attackers', short: 'ATK' },
  { id: 'combat_block', label: 'Blockers', short: 'BLK' },
  { id: 'combat_damage', label: 'Damage', short: 'DMG' },
  { id: 'main2', label: 'Second Main', short: 'M2' },
  { id: 'end', label: 'End Step', short: 'END' },
];

export type FormatId =
  | 'commander'
  | 'two_headed_giant'
  | 'brawl'
  | 'standard'
  | 'modern'
  | 'pioneer'
  | 'legacy'
  | 'vintage'
  | 'pauper'
  | 'custom';

export type SeatStatus = 'empty' | 'occupied' | 'ready' | 'playing' | 'brb' | 'afk' | 'eliminated';

export type SessionType = 'table' | 'private' | 'spectator' | 'overlay';

export type AuthProviderId = 'deckstr' | 'twitch' | 'discord';

export type FlagReason = 'respond' | 'trigger' | 'minute' | 'judge';

export interface TokenTemplate {
  name: string;
  typeLine: string;
  power?: string;
  toughness?: string;
  colors: string[];
  oracleText?: string;
  imageUrl?: string;
}

export interface CardDefinition {
  scryfallId: string;
  name: string;
  manaCost: string;
  cmc: number;
  typeLine: string;
  oracleText: string;
  colors: string[];
  power?: string;
  toughness?: string;
  loyalty?: string;
  imageUrl: string;
  backImageUrl?: string;
  backName?: string;
  backTypeLine?: string;
  backOracleText?: string;
  layout: CardLayout;
  setCode?: string;
  collectorNumber?: string;
  foil?: boolean;
  isGameChanger?: boolean;
  relatedTokens?: TokenTemplate[];
}

export interface CardInstance {
  instanceId: string;
  card: CardDefinition;
  ownerSeatId: string;
  controllerSeatId: string;
  zone: ZoneId;
  tapped: boolean;
  faceDown: boolean;
  flipped: boolean;
  counters: Record<string, number>;
  attachedToId: string | null;
  exiledByInstanceId: string | null;
  row: BattlefieldRow;
  freePos: { x: number; y: number } | null;
  peekedBySeatIds: string[];
  isToken: boolean;
  isCommander: boolean;
  isTemporaryPaperAdd?: boolean;
  enteredZoneSeq: number;
}

/**
 * Redacted card representation for hidden face-down permanents or face-down exile
 * when viewed by someone not permitted to know its identity.
 */
export interface PublicCardView {
  instanceId: string;
  ownerSeatId: string;
  controllerSeatId: string;
  zone: ZoneId;
  tapped: boolean;
  faceDown: boolean;
  flipped: boolean;
  counters: Record<string, number>;
  attachedToId: string | null;
  exiledByInstanceId: string | null;
  row: BattlefieldRow;
  freePos: { x: number; y: number } | null;
  isToken: boolean;
  isCommander: boolean;
  enteredZoneSeq: number;
  /** Present ONLY when viewer is allowed to see the card face; null when sleeve-masked */
  card: CardDefinition | null;
}

export interface PrivateLookState {
  kind: 'scry' | 'look_top' | 'search_library';
  count: number;
  cardInstanceIds: string[];
}

export interface LibraryAccessGrant {
  granteeSeatId: string;
  scope: 'top' | 'entire';
  count: number;
  takeFaceDown: boolean;
  cardInstanceIds: string[];
  grantedAt: number;
}

export interface RuleZeroChecklist {
  targetBracket: 1 | 2 | 3 | 4 | 5 | null;
  fastMana: boolean;
  infiniteCombos: boolean;
  massLandDenial: boolean;
  stax: boolean;
  proxiesAllowed: boolean;
  houseRulesNote: string;
  agreedSeatIds: string[];
}

export interface PregameCeremonyState {
  ruleZero: RuleZeroChecklist;
  highRollResults: Record<string, number>;
  seatingMode: 'manual' | 'magical_chairs' | 'high_roll';
  commandersRevealed: boolean;
}

export interface DeckSummaryMeta {
  deckId?: string;
  name: string;
  mainCount: number;
  commanderNames: string[];
  colorIdentity: string[];
  legalCosmeticArt: {
    cardName: string;
    scryfallId: string;
    artCropUrl: string;
    colors: string[];
    isCommander: boolean;
  }[];
  sideboardCount: number;
  maybeboardCount: number;
  gameChangerCount: number;
  detectedBracket: 1 | 2 | 3 | 4 | 5;
  showTransparency: boolean;
}

export interface PlayerSeat {
  seatId: string;
  seatIndex: number;
  accountId: string | null;
  publicId: string | null;
  displayName: string;
  avatarUrl: string | null;
  profileColor: string;
  matBgCss: string;
  matBorderCss: string;
  verifiedProvider: AuthProviderId | null;
  teamId: string | null;
  status: SeatStatus;
  connected: boolean;
  awaySince: number | null;
  streamMode: boolean;
  hideClickedCards: boolean;
  /** Validated Scryfall art_crop URL from deck-legal pool ONLY (never external upload); null = classic 5-pip card back */
  sleeveUrl: string | null;
  sleeveCardName: string | null;
  /** Validated Scryfall art_crop URL from deck-legal pool ONLY (never external upload); null = distinct deck-identity mat */
  playmatUrl: string | null;
  playmatCardName: string | null;
  playmatFocalPoint: PlaymatFocalPoint;
  life: number;
  startingLife: number;
  playerCounters: Record<string, number>;
  manaPool: Record<ManaPoolColor, number>;
  privateNotes: string;
  savedForLater: string[];
  /** key: `${sourceSeatId}:${commanderInstanceId}` */
  commanderDamage: Record<string, number>;
  /** key: commanderInstanceId -> cast/tax count (each +1 = +2 generic mana) */
  commanderTax: Record<string, number>;
  mulliganCount: number;
  handConfirmed: boolean;
  playWithTopRevealed: boolean;
  revealHandsPostGame: boolean;
  stopPresets: GamePhase[];
  flagMuted: boolean;
  lastFlagClearedAt: number | null;
  privateLook: PrivateLookState | null;
  grantedLibraryAccess: LibraryAccessGrant | null;
  deckMeta: DeckSummaryMeta | null;
  availableTokens: TokenTemplate[];
  paperMode: PaperSeatConfig;
  eliminatedInfo: {
    turn: number;
    reason: 'life' | 'poison' | 'commander_damage' | 'concede' | 'table';
    eliminatedBySeatId: string | null;
  } | null;
}

export type PaperCameraMode = 'off' | 'device' | 'phone_qr' | 'low_bandwidth_still';

export interface PaperVideoMarker {
  markerId: string;
  label: string;
  kind: 'counter' | 'tapped' | 'damage' | 'note';
  /** Normalized 0..100 coordinates on the Table-Only playmat video frame */
  xPct: number;
  yPct: number;
  count?: number;
}

export interface PaperScannedCardEntry {
  scanId: string;
  cardName: string;
  scryfallId: string;
  artistCredit?: string | null;
  zone: 'battlefield' | 'graveyard' | 'exile' | 'command' | 'revealed';
  timestamp: number;
}

export interface RegisteredPaperAlter {
  cardName: string;
  artistCredit: string;
  dHashHex: string;
  registeredAt: number;
}

export interface PaperSeatConfig {
  enabled: boolean;
  cameraMode: PaperCameraMode;
  consentAccepted: boolean;
  aspectRatio: '16:9' | '4:3';
  preferredCodec: 'vp9' | 'vp8' | 'h264';
  lowBandwidthFps: number;
  /** Crop percentages (0..40 each side) for Table-Only privacy framing */
  tableOnlyCrop: { top: number; right: number; bottom: number; left: number };
  privacyBlur: boolean;
  videoMarkers: PaperVideoMarker[];
  scannedCardsSeen: PaperScannedCardEntry[];
  registeredAlters: RegisteredPaperAlter[];
}

export interface InteractionFlag {
  flagId: string;
  seatId: string;
  reason: FlagReason;
  order: number;
  status: 'open' | 'seen';
  createdAt: number;
}

export interface TableSettings {
  name: string;
  format: FormatId;
  maxSeats: number;
  isPublic: boolean;
  isTracked: boolean;
  competitiveMode: boolean;
  startingLife: number;
  openingHandSize: number;
  freeFirstMulligan: boolean;
  useCommandZone: boolean;
  useCommanderDamage: boolean;
  teamMode: boolean;
  sharedTeamLife: boolean;
  simultaneousTeamTurns: boolean;
  teammatesSeeHands: boolean;
  flagGateMode: 'phase_and_turn' | 'turn_only';
  forceStreamMode: boolean;
  forceDefaultCosmetics: boolean;
  removeCardsOnElimination: boolean;
  spectatorsAllowed: boolean;
  allowSpectatorChat?: boolean;
  allowLateJoinRequests?: boolean;
  hasPassword?: boolean;
  bracket?: 1 | 2 | 3 | 4 | 5 | null;
  language?: string;
  description?: string;
  spectatorCap: number;
  spectatorDelaySec: number;
  region: 'auto' | 'us-east' | 'us-west';
}

export interface DesignationsState {
  monarchSeatId: string | null;
  initiativeSeatId: string | null;
  dayNight: 'day' | 'night' | null;
  citysBlessingSeatIds: string[];
  ringBearerBySeat: Record<string, string | null>;
  ringTemptCountBySeat: Record<string, number>;
  undercityRoomBySeat: Record<string, string | null>;
}

export interface ChatMessage {
  id: string;
  channel: 'table' | 'spectator';
  senderPublicId: string;
  senderName: string;
  seatId: string | null;
  text: string;
  whisperToSeatId?: string | null;
  replyToId?: string | null;
  reactions?: Record<string, string[]>;
  editedAt?: number | null;
  deleted?: boolean;
  createdAt: number;
}

export interface LateJoinRequest {
  requestId: string;
  accountId?: string;
  publicId: string;
  displayName: string;
  createdAt: number;
}

export interface TurnSnapshot {
  previousActiveSeatId: string;
  previousTurnNumber: number;
  previousPhase: GamePhase;
}

export interface GameState {
  tableId: string;
  inviteCode: string;
  hostAccountId: string;
  judgeAccountIds: string[];
  locked: boolean;
  status: 'lobby' | 'pregame' | 'active' | 'paused' | 'finished' | 'abandoned';
  settings: TableSettings;
  lateJoinRequests: LateJoinRequest[];
  seats: Record<string, PlayerSeat>;
  seatOrder: string[];
  turnDirection: 1 | -1;
  activeSeatId: string;
  phase: GamePhase;
  turnNumber: number;
  extraTurnsQueue: string[];
  skipTurnSeatIds: string[];
  awayVotes: Record<string, { action: 'skip_turn' | 'remove'; voterSeatIds: string[] }>;
  pregame: PregameCeremonyState;
  lastTurnSnapshot: TurnSnapshot | null;
  /** Canonical card store indexed by instanceId */
  cards: Record<string, CardInstance>;
  /** Ordered instanceIds per seat for ordered zones */
  zoneOrder: Record<string, Record<ZoneId, string[]>>;
  sharedZones: {
    stack: string[];
    revealed: string[];
  };
  flags: InteractionFlag[];
  designations: DesignationsState;
  sharedNotes: string;
  chat: ChatMessage[];
  winnerSeatIds: string[];
  seq: number;
  createdAt: number;
  updatedAt: number;
}

export interface ImportedDeckPayload {
  deckId?: string;
  name: string;
  format?: FormatId;
  mainboard: { card: CardDefinition; quantity: number }[];
  commanders: CardDefinition[];
  companions?: CardDefinition[];
  sideboard: { card: CardDefinition; quantity: number }[];
  maybeboard: { card: CardDefinition; quantity: number }[];
}

// ============================================================================
// INTENTS (Client -> Server)
// ============================================================================

export type PlaymatFocalPoint = 'top' | 'center' | 'bottom';

export type GameIntent =
  | { type: 'SET_SEAT_STATUS'; status: 'ready' | 'playing' | 'brb' | 'afk' }
  | {
      type: 'LOAD_DECK';
      deck: ImportedDeckPayload;
      showTransparency?: boolean;
      cosmetics?: {
        sleeveCardName?: string | null;
        playmatCardName?: string | null;
        focalPoint?: PlaymatFocalPoint;
      };
    }
  | { type: 'LOAD_CUBE_POOL'; cube: ImportedDeckPayload; mode: 'sealed' | 'battlebox'; cardsPerSeat?: number }
  | { type: 'START_MATCH'; autoShuffle?: boolean }
  | { type: 'RESET_MATCH' }
  | { type: 'ABANDON_MATCH' }
  | { type: 'MULLIGAN'; keepCount?: number }
  | { type: 'CONFIRM_OPENING_HAND'; bottomCardIds?: string[] }
  | { type: 'UPDATE_TABLE_SETTINGS'; patch: Partial<TableSettings> }
  | { type: 'TOGGLE_STREAM_MODE'; enabled: boolean }
  | { type: 'TOGGLE_HIDE_CLICKED_CARDS'; enabled: boolean }
  | {
      type: 'SET_SEAT_COSMETICS';
      /** null resets to classic original 5-pip card back; string must match a card in seat's legal deck/identity pool */
      sleeveCardName?: string | null;
      /** null resets to distinct deck-identity color mat; string must match a card in seat's legal deck/identity pool */
      playmatCardName?: string | null;
      playmatFocalPoint?: PlaymatFocalPoint;
    }
  | { type: 'RESET_SEAT_COSMETICS'; targetSeatId: string }
  | {
      type: 'MOVE_CARD';
      instanceId: string;
      toZone: ZoneId;
      toSeatId?: string;
      position?: 'top' | 'bottom' | number;
      row?: BattlefieldRow;
      freePos?: { x: number; y: number } | null;
      faceDown?: boolean;
      tapped?: boolean;
      exiledByInstanceId?: string | null;
    }
  | { type: 'TAP_CARD'; instanceId: string; tapped?: boolean }
  | { type: 'UNTAP_ALL' }
  | { type: 'FLIP_CARD'; instanceId: string }
  | { type: 'TOGGLE_FACE_DOWN'; instanceId: string }
  | { type: 'PEEK_FACE_DOWN'; instanceId: string }
  | { type: 'ATTACH_CARD'; instanceId: string; targetInstanceId: string | null }
  | { type: 'CHANGE_CONTROLLER'; instanceId: string; newControllerSeatId: string }
  | { type: 'ADJUST_CARD_COUNTER'; instanceId: string; counter: string; delta: number }
  | { type: 'CREATE_TOKEN'; token: TokenTemplate; count: number; copyOfInstanceId?: string }
  | { type: 'DRAW_CARDS'; count: number; fromBottom?: boolean }
  | { type: 'MILL_CARDS'; count: number }
  | { type: 'SCRY_START'; count: number }
  | { type: 'SCRY_RESOLVE'; topIds: string[]; bottomIds: string[] }
  | { type: 'LOOK_TOP_START'; count: number }
  | { type: 'REVEAL_TOP_CARD' }
  | { type: 'SEARCH_LIBRARY_START' }
  | {
      type: 'SEARCH_LIBRARY_PICK';
      instanceId: string;
      destination: 'hand' | 'battlefield' | 'top' | 'graveyard' | 'exile';
      faceDown?: boolean;
      shuffleAfter: boolean;
    }
  | { type: 'CLOSE_PRIVATE_LOOK'; shuffleAfter?: boolean }
  | { type: 'SHUFFLE_LIBRARY' }
  | { type: 'TOGGLE_PLAY_TOP_REVEALED'; enabled: boolean }
  | { type: 'ADJUST_LIFE'; targetSeatId?: string; delta: number }
  | { type: 'ADJUST_PLAYER_COUNTER'; targetSeatId?: string; counter: string; delta: number }
  | {
      type: 'ADJUST_COMMANDER_DAMAGE';
      targetSeatId: string;
      sourceSeatId: string;
      commanderInstanceId: string;
      delta: number;
    }
  | { type: 'ADJUST_COMMANDER_TAX'; commanderInstanceId: string; delta: number }
  | { type: 'SET_DESIGNATION'; designation: 'monarch' | 'initiative' | 'day' | 'night' | 'citys_blessing'; seatId?: string | null }
  | { type: 'SET_PHASE'; phase: GamePhase }
  | { type: 'ADVANCE_PHASE' }
  | { type: 'PASS_TURN' }
  | { type: 'UNDO_PASS_TURN' }
  | { type: 'REVERSE_TURN_DIRECTION' }
  | { type: 'RAISE_FLAG'; reason: FlagReason }
  | { type: 'MARK_FLAG_SEEN'; flagId: string }
  | { type: 'CLEAR_FLAG'; flagId: string }
  | { type: 'ELIMINATE_PLAYER'; targetSeatId: string; reason: 'life' | 'poison' | 'commander_damage' | 'concede' | 'table'; eliminatedBySeatId?: string | null }
  | { type: 'UNDO_LAST_ACTION' }
  | { type: 'ROLLBACK_TO_SEQ'; targetSeq: number }
  | { type: 'ROLL_DIE'; sides: number }
  | { type: 'FLIP_COIN' }
  | { type: 'UPDATE_SHARED_NOTES'; notes: string }
  | {
      type: 'SEND_CHAT';
      text: string;
      channel?: 'table' | 'spectator';
      whisperToSeatId?: string | null;
      replyToId?: string | null;
    }
  | { type: 'EDIT_CHAT'; messageId: string; newText: string }
  | { type: 'DELETE_CHAT'; messageId: string }
  | { type: 'REACT_CHAT'; messageId: string; emoji: string }
  | { type: 'PAUSE_SAVE_MATCH' }
  | { type: 'RESUME_SAVED_MATCH' }
  | { type: 'TOGGLE_POSTGAME_HAND_REVEAL'; enabled: boolean }
  | { type: 'INSPECT_CARD'; instanceId: string }
  | { type: 'REORDER_SEATS'; newSeatOrder: string[] }
  | { type: 'ASSIGN_TEAM'; targetSeatId: string; teamId: string | null }
  | { type: 'ADD_EXTRA_TURN'; targetSeatId?: string }
  | { type: 'SKIP_NEXT_TURN'; targetSeatId: string }
  | { type: 'VOTE_AWAY_PLAYER'; targetSeatId: string; action: 'skip_turn' | 'remove' }
  | { type: 'REMATCH_POD'; reshuffleSeats?: boolean }
  | { type: 'UPDATE_RULE_ZERO'; patch: Partial<Omit<RuleZeroChecklist, 'agreedSeatIds'>> }
  | { type: 'AGREE_RULE_ZERO'; agreed?: boolean }
  | { type: 'RUN_PREGAME_SEATING'; mode: 'magical_chairs' | 'high_roll' }
  | { type: 'REVEAL_COMMANDERS_PREGAME' }
  | { type: 'SET_STOP_PRESETS'; phases: GamePhase[] }
  | { type: 'SET_FLAG_MUTE'; targetSeatId: string; muted: boolean }
  | {
      type: 'GRANT_LIBRARY_ACCESS';
      granteeSeatId: string;
      scope: 'top' | 'entire';
      count?: number;
      takeFaceDown: boolean;
    }
  | {
      type: 'TAKE_FROM_GRANTED_LIBRARY';
      ownerSeatId: string;
      instanceId: string;
      destination?: 'exile' | 'hand' | 'battlefield';
    }
  | { type: 'REVOKE_LIBRARY_ACCESS'; ownerSeatId?: string }
  | { type: 'ADJUST_MANA_POOL'; color: ManaPoolColor; delta: number }
  | { type: 'CLEAR_MANA_POOL' }
  | { type: 'SET_RING_BEARER'; instanceId: string | null; temptDelta?: number }
  | { type: 'SET_UNDERCITY_ROOM'; room: string | null }
  | { type: 'REVEAL_UNTIL'; maxCmc?: number; cardType?: string; nonLand?: boolean; privateOnly?: boolean }
  | {
      type: 'RESOLVE_REVEALED_UNTIL';
      hitInstanceId?: string | null;
      hitDestination: 'battlefield' | 'hand' | 'stack' | 'exile';
      bottomOrder?: 'random' | 'current';
    }
  | {
      type: 'PICK_RANDOM_TARGET';
      scope: 'opponent' | 'any_player' | 'my_permanents' | 'all_permanents' | 'my_hand';
    }
  | { type: 'UPDATE_PRIVATE_NOTES'; notes: string }
  | { type: 'TOGGLE_SAVE_FOR_LATER'; cardName: string }
  | {
      type: 'CONFIGURE_PAPER_MODE';
      enabled?: boolean;
      cameraMode?: PaperCameraMode;
      consentAccepted?: boolean;
      aspectRatio?: '16:9' | '4:3';
      preferredCodec?: 'vp9' | 'vp8' | 'h264';
      lowBandwidthFps?: number;
      tableOnlyCrop?: { top: number; right: number; bottom: number; left: number };
      privacyBlur?: boolean;
    }
  | {
      type: 'CONFIRM_PAPER_SCAN';
      cardName: string;
      toZone?: 'battlefield' | 'graveyard' | 'exile' | 'command' | 'revealed';
      asTokenCopy?: boolean;
      count?: number;
    }
  | {
      type: 'ADD_PAPER_CARD_LIVE';
      card: CardDefinition;
      scope: 'this_game_only' | 'save_deck_update';
      toZone?: 'battlefield' | 'hand' | 'library' | 'command';
    }
  | {
      type: 'SET_VIDEO_MARKER';
      targetSeatId?: string;
      markerId?: string;
      label: string;
      kind: PaperVideoMarker['kind'];
      xPct: number;
      yPct: number;
      count?: number;
    }
  | {
      type: 'CLEAR_VIDEO_MARKER';
      targetSeatId?: string;
      markerId: string | 'all';
    }
  | {
      type: 'REGISTER_PAPER_ALTER';
      cardName: string;
      artistCredit: string;
      dHashHex: string;
    };

// ============================================================================
// APPEND-ONLY EVENTS (Authoritative Event Log)
// ============================================================================

export interface GameEvent {
  seq: number;
  timestamp: number;
  turnNumber: number;
  actorSeatId: string | null;
  actorName: string;
  type: string;
  summary: string;
  /** Publicly safe structured payload for UI animations & client feed */
  payload: Record<string, unknown>;
  /**
   * Internal deterministic state-transition payload stored ONLY on server event log
   * (contains e.g. post-shuffle permutation or drawn instanceId so replayEvents() is 100% deterministic
   * while never leaking hidden order to unauthorized clients).
   */
  internalDelta?: Record<string, unknown>;
}

// ============================================================================
// FILTERED CLIENT VIEW (Server -> Specific Connection)
// ============================================================================

export interface ClientSeatView {
  seatId: string;
  seatIndex: number;
  publicId: string | null;
  displayName: string;
  avatarUrl: string | null;
  profileColor: string;
  matBgCss: string;
  matBorderCss: string;
  verifiedProvider: AuthProviderId | null;
  teamId: string | null;
  status: SeatStatus;
  connected: boolean;
  awaySince: number | null;
  streamMode: boolean;
  sleeveUrl: string | null;
  sleeveCardName: string | null;
  playmatUrl: string | null;
  playmatCardName: string | null;
  playmatFocalPoint: PlaymatFocalPoint;
  life: number;
  startingLife: number;
  playerCounters: Record<string, number>;
  manaPool: Record<ManaPoolColor, number>;
  privateNotes: string | null;
  savedForLater: string[] | null;
  commanderDamage: Record<string, number>;
  commanderTax: Record<string, number>;
  mulliganCount: number;
  handConfirmed: boolean;
  playWithTopRevealed: boolean;
  revealHandsPostGame: boolean;
  stopPresets: GamePhase[];
  flagMuted: boolean;
  hasGrantedLibraryAccessTo: {
    granteeSeatId: string;
    scope: 'top' | 'entire';
    count: number;
    takeFaceDown: boolean;
  } | null;
  deckMeta: DeckSummaryMeta | null;
  availableTokens: TokenTemplate[];
  paperMode: PaperSeatConfig;
  eliminatedInfo: PlayerSeat['eliminatedInfo'];
  /** Zone summaries — hidden zones expose counts ONLY, never hidden CardDefinitions */
  zones: {
    libraryCount: number;
    /** Exposed ONLY when playWithTopRevealed is true (and not masked for viewer) */
    libraryTopCard: PublicCardView | null;
    handCount: number;
    /** Populated ONLY if viewer is authorized to see this seat's hand */
    handCards: PublicCardView[] | null;
    battlefield: PublicCardView[];
    graveyard: PublicCardView[];
    exile: PublicCardView[];
    command: PublicCardView[];
    sideboardCount: number;
    sideboardCards: PublicCardView[] | null;
  };
  /** Public indicator that seat is currently scrying/searching without revealing which cards */
  activePrivateAction: { kind: PrivateLookState['kind']; count: number } | null;
  /** Populated ONLY on authorized private/non-stream owner session */
  privateLookCards: PublicCardView[] | null;
}

export interface PublicLogEntry {
  seq: number;
  timestamp: number;
  turnNumber: number;
  actorSeatId: string | null;
  actorName: string;
  type: string;
  summary: string;
  animation?: {
    kind: 'card_move' | 'tap' | 'shuffle' | 'life' | 'eliminate' | 'dice' | 'flag';
    instanceId?: string;
    cardName?: string | null;
    cardImageUrl?: string | null;
    fromZone?: ZoneId;
    toZone?: ZoneId;
    fromSeatId?: string;
    toSeatId?: string;
  };
}

export interface ClientGameView {
  tableId: string;
  inviteCode: string;
  isHost: boolean;
  isJudge: boolean;
  viewerSeatId: string | null;
  viewerSessionType: SessionType;
  locked: boolean;
  status: GameState['status'];
  settings: TableSettings;
  lateJoinRequests: Omit<LateJoinRequest, 'accountId'>[];
  seatOrder: string[];
  activeSeatOrder: string[];
  turnDirection: 1 | -1;
  activeSeatId: string;
  phase: GamePhase;
  turnNumber: number;
  extraTurnsQueue: string[];
  skipTurnSeatIds: string[];
  pregame: PregameCeremonyState;
  grantedLibraryViews: {
    ownerSeatId: string;
    ownerName: string;
    scope: 'top' | 'entire';
    takeFaceDown: boolean;
    cards: PublicCardView[];
  }[];
  canUndoPassTurn: boolean;
  seats: Record<string, ClientSeatView>;
  sharedZones: {
    stack: PublicCardView[];
    revealed: PublicCardView[];
  };
  flags: InteractionFlag[];
  designations: DesignationsState;
  sharedNotes: string;
  chat: ChatMessage[];
  spectatorCount: number;
  winnerSeatIds: string[];
  recentLog: PublicLogEntry[];
  seq: number;
}

export type TrackingProviderId = 'play_nice' | 'mythic_track' | 'topdeck';

export interface DeckStrengthSummary {
  deckId: string;
  eraNumber: number;
  bracket: 1 | 2 | 3 | 4 | 5;
  trackedGames: number;
  gated: boolean;
  /** e.g. "B3 · 4/10 tracked" when < 10 games, or "1.8K · T9 · B4 · 32%" once >= 10 games */
  badge: string;
  rating: number | null;
  medianWinTurn: number | null;
  winRatePct: number | null;
}

export interface FormatLeaderboardEntry {
  rank: number;
  publicId: string;
  displayName: string;
  avatarUrl: string | null;
  profileColor: string;
  format: FormatId;
  rating: number;
  games90d: number;
  distinctDecks90d: number;
  winRatePct: number;
}
